import React from 'react';
import { Link } from 'react-router-dom';
import { DocPage, Note, Steps, WqTable, Figure } from '../../components/documentation/web-integration';
import GetStartedImage from '../../assets/docs/get-started.png';

const CONTENTS = ['Overview', 'Register', 'Verify your email', 'Log in', 'Your dashboard', 'Secure your account', 'Activate your account'];

const REGISTER_STEPS = [
    <>
        Click <strong>Register</strong> at the top-right corner of this page.
    </>,
    <>Enter your details: your name, email address, phone number, business name and a password.</>,
    <>Read and accept the Terms and Conditions.</>,
    <>
        Click <strong>Create account</strong>.
    </>,
];

const KYC_STEPS = [
    <>
        <strong>Personal details.</strong> Confirm your details and verify your BVN — a code is sent to the phone
        number linked to your BVN. Your date of birth and gender are filled in from your BVN record.
    </>,
    <>
        <strong>Business details.</strong> Your business name, type, industry and contact details.
    </>,
    <>
        <strong>Directors.</strong> Details and identity documents for at least 1 director,owner or sharholder of the business.
    </>,
    <>
        <strong>Certificates.</strong> Upload your registration documents.
    </>,
    <>
        <strong>Review and submit.</strong> Check the summary and submit your KYC for review.
    </>,
];

const KYC_STATUS = {
    columns: ['Status', 'What it means'],
    rows: [
        ['Submitted', "We're reviewing your KYC. Your dashboard updates by itself when there's a decision."],
        ['Approved', 'Your account is active. Switch to Live mode to start collecting real payments.'],
        ['Rejected', "You'll see the reason. Fix the step it mentions and submit again."],
    ],
};

function DocsGuide() {
    return (
        <DocPage
            seo={{
                title: 'Registration Guide',
                description:
                    'Step-by-step guide to registering a WayaQuick merchant account, verifying your email, logging in and activating your account with KYC.',
            }}
            eyebrow="Merchants · Account setup"
            title={
                <>
                    WayaQuick — <em>Registration Guide</em>
                </>
            }
            contents={CONTENTS}
            footer="WayaQuick · Registration Guide · Register, test for free, then activate your account to go live."
            prev={{ to: '/get-started', label: 'Get Started' }}
            next={{ to: '/payment-life-cycle', label: 'Payment Life Cycle' }}
        >
            <section className="wq-section" id={CONTENTS[0]}>
                <h2 className="wq-h2">Overview</h2>
                <p className="wq-lead">
                    Registering gives you a WayaQuick merchant dashboard straight away, in Test mode. Activating your
                    account with KYC unlocks Live mode, so you can collect real payments and receive settlements.
                </p>
                <WqTable
                    columns={['Stage', 'What you get']}
                    rows={[
                        [<strong>Register</strong>, 'Your merchant dashboard in Test mode, to try every feature for free.'],
                        [<strong>Complete KYC</strong>, 'Your business is verified so you can accept real payments.'],
                        [<strong>Go live</strong>, 'Collect real payments and get them settled to your bank account.'],
                    ]}
                />
            </section>

            <section className="wq-section" id={CONTENTS[1]}>
                <h2 className="wq-h2">Register</h2>
                <Steps items={REGISTER_STEPS} />
            </section>

            <section className="wq-section" id={CONTENTS[2]}>
                <h2 className="wq-h2">Verify your email</h2>
                <p className="wq-p">
                    We send a one-time code to your email address. Enter it to verify your email. If it doesn't arrive, check your spam folder or request a new code.
                </p>
            </section>

            <section className="wq-section" id={CONTENTS[3]}>
                <h2 className="wq-h2">Log in</h2>
                <p className="wq-p">
                    Once your email is verified, we email you your <strong>Login ID</strong>. You log in with your Login
                    ID and password.
                </p>
                <Note label="Keep your Login ID handy">
                    Your Login ID is a long number, so keep the email for subsequent login success.
                </Note>
            </section>

            <section className="wq-section" id={CONTENTS[4]}>
                <h2 className="wq-h2">Your dashboard</h2>
                <p className="wq-p">
                    Your dashboard opens in <strong>Test mode</strong>, shown by the Test / Live toggle at the top. In
                    Test mode you can create payment links, try the checkout and build your integration without moving
                    real money. The toggle stays on Test until your KYC is approved.
                </p>
                <Figure src={GetStartedImage} alt="WayaQuick merchant dashboard" />
                <p className="wq-p">
                    Learn more in the{' '}
                    <Link to="/test-environment" className="wq-link">
                        Test Environment
                    </Link>{' '}
                    guide.
                </p>
            </section>

            <section className="wq-section" id={CONTENTS[5]}>
                <h2 className="wq-h2">Secure your account</h2>
                <ul className="wq-p wq-list">
                    <li>
                        <strong>Transaction PIN.</strong> After you log in, you're asked to create a 4-digit PIN. You
                        need it to approve payouts. If you skip it, add it later under Settings › Profile ›{' '}
                        <strong>Add Pin</strong>.
                    </li>
                    <li>
                        <strong>Two-factor authentication (2FA).</strong> Turn it on in your profile settings to get a
                        code by email each time you log in and before sensitive actions.
                    </li>
                </ul>
            </section>

            <section className="wq-section" id={CONTENTS[6]}>
                <h2 className="wq-h2">Activate your account</h2>
                <p className="wq-p">
                    To collect real payments, complete your KYC (Know Your Customer) from your dashboard. You can save
                    each step and come back to it later. There are five steps:
                </p>
                <Steps items={KYC_STEPS} />

                <Note label="Uploading documents">
                    Each file must be 1.5 MB or smaller. The documents you need depend on how your business is registered
                    — the form shows exactly what's required.
                </Note>

                <h4 className="wq-h4">After you submit</h4>
                <WqTable columns={KYC_STATUS.columns} rows={KYC_STATUS.rows} />
                <p className="wq-p">
                    Once you're approved, switch the toggle to Live, generate your live API keys if you're integrating,
                    and add a settlement account under Settings › WayaQuick Settings › Settlement Account so your
                    payments can be paid out to you.
                </p>
            </section>
        </DocPage>
    );
}

export default DocsGuide;
